import type { WorkflowView } from '@/lib/api/generated/model';

/**
 * Keyboard traversal for the read-only graph (FR-058).
 *
 * The read-only graph had no keyboard navigation at all — `nodesFocusable` and
 * `edgesFocusable` were both off. Making nodes focusable gets a user *to* a
 * node; this gets them *along* the graph, which is the part a mouse does by
 * following an arrow with the eye.
 *
 * **Why cycling needs memory.** A step may have several outgoing transitions,
 * and FR-058 requires cycling among its targets. The obvious implementation —
 * "select the next target" — cannot cycle: the moment the first press lands on
 * target A, the selected step *is* target A, so a second press would traverse
 * A's targets instead of continuing through the fork. Cycling is only
 * expressible if the source step is remembered across presses, which is what
 * :type:`TraversalCursor` is for.
 *
 * Pure and exported so it can be unit-tested without a canvas: jsdom renders no
 * layout, and a test driving this through React Flow would be a test of the
 * mock (research D14).
 */

/**
 * Where a cycle is up to: the step being cycled *from*, and how far round.
 *
 * `null` means no cycle is in progress — the next forward press starts one.
 */
export type TraversalCursor = { fromStepId: string; index: number } | null;

export type TraversalStep = {
  /** The step to select, or `null` to leave the selection alone. */
  selectStepId: string | null;
  cursor: TraversalCursor;
};

/** Every step id a transition may name, including the two system nodes. */
function targetsOf(workflow: WorkflowView, stepId: string): string[] {
  // `$start` is not a declared step, so its one transition is not in `phases`.
  // Reading it from `workflow.start` keeps Backlog a real place to traverse from
  // rather than a dead end the keyboard cannot leave.
  if (stepId === workflow.start_step.id) {
    return workflow.start ? [workflow.start] : [];
  }

  for (const phase of workflow.phases ?? []) {
    for (const step of phase.steps ?? []) {
      if (step.id === stepId) {
        return (step.transitions ?? []).map((transition) => transition.to);
      }
    }
  }
  return [];
}

/**
 * Advance one step forward along the graph, cycling a fork's branches.
 *
 * From a step with one way out this is simply "follow it". From a fork, the
 * first press takes the first branch and each further press swaps to the next,
 * wrapping — so every branch is reachable without a pointer, and the user can
 * see where each one goes before moving on.
 *
 * `direction` runs the cycle backwards, so a user who overshoots on a wide fork
 * does not have to wrap all the way round.
 */
export function traverseForward(
  workflow: WorkflowView,
  selectedStepId: string | undefined,
  cursor: TraversalCursor,
  direction: 1 | -1 = 1,
): TraversalStep {
  if (selectedStepId === undefined) return { selectStepId: null, cursor: null };

  // Mid-cycle: the selection is a branch of `cursor.fromStepId`, so the source
  // to cycle is that step, not the target we are standing on.
  const cycling =
    cursor !== null &&
    targetsOf(workflow, cursor.fromStepId).includes(selectedStepId);

  const sourceId = cycling ? cursor.fromStepId : selectedStepId;
  const targets = targetsOf(workflow, sourceId);
  if (targets.length === 0) return { selectStepId: null, cursor: null };

  const nextIndex = cycling
    ? // `+ targets.length` keeps the modulo positive when stepping backwards.
      (cursor.index + direction + targets.length) % targets.length
    : direction === 1
      ? 0
      : targets.length - 1;

  return {
    selectStepId: targets[nextIndex],
    // Only a fork is worth remembering. With one way out there is nothing to
    // cycle, and holding a cursor would make the *next* press cycle a step the
    // user has already left.
    cursor:
      targets.length > 1 ? { fromStepId: sourceId, index: nextIndex } : null,
  };
}
